import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  achievementTypeConfig,
  locationConfig,
  achievementStats,
  liveAchievements,
  achievementRules,
  getAchievementsByType,
  getAchievementsByLocation,
  getRecentAchievements,
  type AchievementType,
  type AchievementLocation,
  type LiveAchievement,
} from '@/mocks/rfwLiveAchievements';

function TypeBadge({ type }: { type: AchievementType }) {
  const c = achievementTypeConfig[type];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${c.bgColor} ${c.color} border border-current/20`}>
      <i className={`${c.icon} text-[8px]`}></i>
      {c.label}
    </span>
  );
}

function LocationBadge({ location }: { location: AchievementLocation }) {
  const c = locationConfig[location];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold ${c.bgColor} ${c.color} border border-current/20`}>
      <i className={`${c.icon} text-[8px]`}></i>
      {c.label}
    </span>
  );
}

function DecayTimer({ decayMinutes }: { decayMinutes: number }) {
  const label = decayMinutes >= 1440
    ? `${Math.floor(decayMinutes / 1440)}d`
    : decayMinutes >= 60
    ? `${Math.floor(decayMinutes / 60)}h`
    : `${decayMinutes}m`;
  return (
    <span className="inline-flex items-center gap-1 text-[8px] text-foreground-400">
      <i className="ri-timer-line text-[7px]"></i>
      ~{label}
    </span>
  );
}

export default function AchievementsEnVivoSection() {
  const [activeTab, setActiveTab] = useState<'logros' | 'ubicaciones' | 'reglas'>('logros');
  const [typeFilter, setTypeFilter] = useState<AchievementType | 'todas'>('todas');
  const [locationFilter, setLocationFilter] = useState<AchievementLocation | 'todas'>('todas');

  const filtered = useMemo(() => {
    let result = typeFilter === 'todas' ? getRecentAchievements() : getAchievementsByType(typeFilter).filter((a) => a.isRecent);
    if (locationFilter !== 'todas') result = result.filter((a) => a.location === locationFilter);
    return result;
  }, [typeFilter, locationFilter]);

  const tabs = [
    { id: 'logros' as const, label: 'En Vivo', icon: 'ri-sparkling-line', badge: getRecentAchievements().length },
    { id: 'ubicaciones' as const, label: 'Ubicaciones', icon: 'ri-map-pin-line', badge: 4 },
    { id: 'reglas' as const, label: 'Reglas', icon: 'ri-scales-line', badge: 3 },
  ];

  const typeFilters = [
    { id: 'todas' as const, icon: 'ri-list-check-3', label: 'Todos' },
    ...Object.entries(achievementTypeConfig).map(([key, c]) => ({
      id: key as AchievementType,
      icon: c.icon,
      label: c.label,
    })),
  ];

  const locationFilters = [
    { id: 'todas' as const, icon: 'ri-list-check-3', label: 'Todas' },
    ...Object.entries(locationConfig).map(([key, c]) => ({
      id: key as AchievementLocation,
      icon: c.icon,
      label: c.label,
    })),
  ];

  return (
    <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-100 text-teal-700 text-xs font-semibold mb-4">
            <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse"></span>
            ACHIEVEMENTS EN VIVO
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Cada logro <span className="text-teal-600">se celebra</span> en el momento
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Nuevos niveles, insignias, récords, rankings y logros aparecen en vivo en las cuatro ubicaciones de RFW — Perfil, Cabina, Feed y Club. Animaciones sutiles que motivan sin interrumpir.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
          {[
            { label: 'Logros Hoy', value: achievementStats.totalToday.toLocaleString(), icon: 'ri-sparkling-line', color: 'text-teal-600 bg-teal-50' },
            { label: 'Niveles', value: achievementStats.porNivel.toString(), icon: 'ri-arrow-up-circle-line', color: 'text-teal-600 bg-teal-50' },
            { label: 'Insignias', value: achievementStats.porInsignia.toString(), icon: 'ri-award-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Récords', value: achievementStats.porRecord.toString(), icon: 'ri-fire-line', color: 'text-rose-600 bg-rose-50' },
            { label: 'Activos Ahora', value: achievementStats.activeAchievements.toString(), icon: 'ri-live-line', color: 'text-emerald-600 bg-emerald-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-100 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-center mb-6">
          <div className="inline-flex bg-background-100 rounded-full p-1 border border-background-200/70">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-primary-500 text-white shadow-sm'
                    : 'text-foreground-600 hover:text-foreground-900'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
                {tab.badge > 0 && (
                  <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-bold ${
                    activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-teal-100 text-teal-600'
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {activeTab === 'logros' && (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center gap-1.5 justify-center">
              {typeFilters.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setTypeFilter(f.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                    typeFilter === f.id
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'bg-background-100 text-foreground-500 border-background-200 hover:border-foreground-300'
                  }`}
                >
                  <i className={`${f.icon} text-[9px]`}></i>
                  {f.label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-1.5 justify-center">
              {locationFilters.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setLocationFilter(f.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                    locationFilter === f.id
                      ? 'bg-primary-500 text-white border-primary-500'
                      : 'bg-background-100 text-foreground-500 border-background-200 hover:border-foreground-300'
                  }`}
                >
                  <i className={`${f.icon} text-[9px]`}></i>
                  {f.label}
                </button>
              ))}
            </div>

            {filtered.length === 0 && (
              <p className="text-center text-xs text-foreground-400 py-8">No hay achievements con estos filtros ahora mismo. ¡Volvé en unos minutos!</p>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filtered.slice(0, 8).map((achievement) => (
                <AchievementCard key={achievement.id} achievement={achievement} />
              ))}
            </div>
          </div>
        )}

        {activeTab === 'ubicaciones' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(locationConfig).map(([key, c]) => {
                const count = getAchievementsByLocation(key as AchievementLocation).length;
                const recentInLocation = getAchievementsByLocation(key as AchievementLocation).filter((a) => a.isRecent);
                return (
                  <div key={key} className={`p-5 rounded-2xl bg-background-100 border border-background-200/70`}>
                    <div className="flex items-start gap-4 mb-4">
                      <div className={`w-14 h-14 rounded-2xl ${c.bgColor} flex items-center justify-center shrink-0`}>
                        <i className={`${c.icon} text-2xl ${c.color}`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-base font-extrabold text-foreground-950 mb-1">{c.label}</h4>
                        <p className="text-[11px] text-foreground-500 leading-relaxed mb-2">{c.description}</p>
                        <div className="flex items-center gap-3 text-[10px]">
                          <span className={`px-2 py-0.5 rounded-full ${c.bgColor} ${c.color} font-semibold`}>
                            {c.style === 'celebratory' ? '🎉 Celebratorio' : c.style === 'moderate' ? '✨ Moderado' : '🔹 Sutil'}
                          </span>
                          <span className="text-foreground-400">Máx {c.maxSimultaneous} simultáneos</span>
                        </div>
                      </div>
                      <div className="text-center shrink-0">
                        <p className="text-2xl font-extrabold text-foreground-950">{count}</p>
                        <p className="text-[9px] text-foreground-400">totales</p>
                        {recentInLocation.length > 0 && (
                          <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold">
                            <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse"></span>
                            {recentInLocation.length} activos
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      {getAchievementsByLocation(key as AchievementLocation).filter((a) => a.isRecent).slice(0, 3).map((a) => (
                        <div key={a.id} className="flex items-center gap-2 p-2 rounded-lg bg-background-50">
                          <img src={a.userPhoto} alt={a.userName} className="w-7 h-7 rounded-full object-cover shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-[10px] font-semibold text-foreground-950 line-clamp-1">{a.title}</p>
                            <p className="text-[8px] text-foreground-400">{a.userName} · {a.achievedAtRelative}</p>
                          </div>
                          <TypeBadge type={a.achievementType} />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'reglas' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {achievementRules.map((rule) => (
                <div key={rule.id} className={`p-5 rounded-2xl ${rule.bgColor} border border-background-200/50 text-center`}>
                  <div className="w-10 h-10 rounded-full bg-background-50 flex items-center justify-center mx-auto mb-2">
                    <i className={`${rule.icon} text-lg ${rule.color}`}></i>
                  </div>
                  <p className="text-sm font-bold text-foreground-950 mb-1">{rule.title}</p>
                  <p className="text-[11px] text-foreground-500">{rule.description}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {Object.entries(achievementTypeConfig).map(([key, c]) => {
                const count = getAchievementsByType(key as AchievementType).length;
                return (
                  <div key={key} className="p-3 rounded-xl bg-background-100 border border-background-200/70 text-center">
                    <div className={`w-8 h-8 rounded-full ${c.bgColor} flex items-center justify-center mx-auto mb-1.5`}>
                      <i className={`${c.icon} text-sm ${c.color}`}></i>
                    </div>
                    <p className="text-[10px] font-semibold text-foreground-700">{c.label}</p>
                    <p className="text-lg font-bold text-foreground-950">{count}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="text-center mt-10">
          <Link
            to="/achievements-en-vivo"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-full transition-all duration-200 text-sm whitespace-nowrap"
          >
            <i className="ri-sparkling-line"></i>
            Ver Todos los Achievements en Vivo
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="mt-10 p-6 rounded-2xl bg-teal-100/70 border border-teal-200/50 text-center">
          <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-sparkling-line text-lg text-white"></i>
          </div>
          <p className="text-sm font-bold text-foreground-950 mb-1">
            Celebrar logros sin ser invasivo.
          </p>
          <p className="text-xs text-foreground-600 max-w-lg mx-auto">
            En RFW cada logro se celebra en el momento justo y en el lugar correcto. Las animaciones son sutiles, los badges no saturan la pantalla y lo más importante — lo que suma al perfil se queda, lo que distrae en el chat desaparece naturalmente. Porque motivar no significa bombardear: significa hacer que cada achievement se sienta importante sin robar protagonismo a lo que de verdad importa — la experiencia de estar en comunidad.
          </p>
        </div>
      </div>
    </section>
  );
}

function AchievementCard({ achievement }: { achievement: LiveAchievement }) {
  const tc = achievementTypeConfig[achievement.achievementType];
  const lc = locationConfig[achievement.location];

  return (
    <Link
      to="/achievements-en-vivo"
      className={`p-4 rounded-xl bg-background-100 border transition-all duration-200 group ${
        achievement.isRecent
          ? 'border-teal-200/70 hover:border-teal-300/60'
          : 'border-background-200/70 hover:border-teal-200/60'
      }`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="relative shrink-0">
          <img
            src={achievement.userPhoto}
            alt={achievement.userName}
            className="w-10 h-10 rounded-full object-cover"
          />
          <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full ${tc.bgColor} flex items-center justify-center border-2 border-background-100`}>
            <i className={`${tc.icon} text-[9px] ${tc.color}`}></i>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-foreground-950 line-clamp-1">
            {achievement.userName} <span className="text-foreground-400 font-normal">{tc.verb}</span> {achievement.title}
          </p>
          <p className="text-[10px] text-foreground-500 line-clamp-1">{achievement.subtitle}</p>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <TypeBadge type={achievement.achievementType} />
            <LocationBadge location={achievement.location} />
            {achievement.isRecent && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-100 text-emerald-700">
                <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse"></span>
                AHORA
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-3">
        {achievement.previousValue && (
          <div className="flex items-center gap-1 text-[9px]">
            <span className="text-foreground-400 line-through">{achievement.previousValue}</span>
            <i className="ri-arrow-right-line text-foreground-400 text-[8px]"></i>
          </div>
        )}
        <span className={`text-[10px] font-extrabold ${tc.color}`}>{achievement.value}</span>
      </div>

      <div className="flex items-center justify-between text-[9px]">
        <span className="text-foreground-400 flex items-center gap-1">
          <i className="ri-time-line text-[8px]"></i>
          {achievement.achievedAtRelative}
        </span>
        <div className="flex items-center gap-3">
          <span className="text-foreground-400 flex items-center gap-1">
            <i className="ri-heart-line text-[8px]"></i>
            {achievement.reactions}
          </span>
          <span className="text-foreground-400 flex items-center gap-1">
            <i className="ri-chat-3-line text-[8px]"></i>
            {achievement.comments}
          </span>
          <DecayTimer decayMinutes={achievement.decayMinutes} />
        </div>
      </div>
    </Link>
  );
}